import axios from 'axios';
import { useState, useRef, useEffect, Fragment } from 'react';
import { useRouter } from 'next/router';
import {
  YMaps,
  Map,
  ZoomControl,
  GeolocationControl,
  Button,
  withYMaps,
  ObjectManager,
  Polygon,
  Placemark
} from 'react-yandex-maps';
import { JSONToQuery } from '../../helpers';
import cleanCoords from '@turf/clean-coords';
import unkinkPolygon from '@turf/unkink-polygon';
import combine from '@turf/combine';
import buffer from '@turf/buffer';
import { useLocationState } from '../../context/location';
import { useObjectsProvider } from '../../context/objects';
import { useIsMount } from '../../hooks/useIsMount';
import ObjectsMapCatalog from './ObjectsMapCatalog';
import ObjectsMapCatalogMobile from './ObjectsMapCatalogMobile';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import styles from '../../styles/modules/objects/ObjectsMap.module.scss';

const TemplateProvider = withYMaps(({ children, template }) => children({ template }), true, [
  'templateLayoutFactory'
]);
const polygonDefaults = {
  strokeColor: '#52bf41',
  strokeWidth: 3.5,
  fillColor: '#52bf41',
  fillOpacity: 0.2,
  interactivityModel: 'default#transparent'
};

const isAnyFilters = (s) => {
  const arrFilters = [
    'priceFrom',
    'priceTo',
    'rooms',
    'studio',
    'squareFrom',
    'squareTo',
    'mikroraion',
    'squareAreaFrom',
    'squareAreaTo',
    'commercialType',
    'squareLivingFrom',
    'squareLivingTo',
    'squareKitchenFrom',
    'squareKitchenTo',
    'floorFrom',
    'floorTo',
    'floorsFrom',
    'floorsTo',
    'ceiling',
    'balcony',
    'renovation',
    'bathroom',
    'buildingType',
    'stock',
    'lotType',
    'subCategory',
    'entrance',
    'commercialBuildingType'
  ];
  let filtersTotal = 0;

  for (let i = 0; i < arrFilters.length; i++)
    if (s[arrFilters[i]] && (s[arrFilters[i]].length || s[arrFilters[i]] > 0)) filtersTotal++;

  return filtersTotal > 0;
};

const ObjectsMap = () => {
  const router = useRouter();
  const isMount = useIsMount();
  const locationState = useLocationState();
  const { width } = useWindowDimensions();
  const [objectsState, setObjectsState] = useObjectsProvider();
  const [ymaps, setYmaps] = useState();
  const [isMapReady, setIsMapReady] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isDrawing, setIsDrawing] = useState(Boolean(objectsState.map.searchZone.length));
  const [drawPolygon, setDrawPolygon] = useState(objectsState.map.searchZone);
  const [objectManagerFeatures, setObjectManagerFeatures] = useState([]);
  const [clickedActivePoint, setClickedActivePoint] = useState();
  const [zoomLevel, setZoomLevel] = useState(locationState.zoom);
  const mapId = useRef();
  const drawButtonId = useRef();

  const drawCustomPolygon = (y) => {
    const rect = y.container.getParentElement().getBoundingClientRect();
    const canvas = document.createElement('canvas');
    const ctx2d = canvas.getContext('2d');

    let isDrawing = false;
    let coordinates = [];
    let offsets = [];

    /** Setting canvas same size as map element. */
    canvas.style.width = `${rect.width} px`;
    canvas.style.height = `${rect.height} px`;
    canvas.style.zIndex = '50000';
    canvas.style.position = 'fixed';
    canvas.style.right = 0;
    canvas.style.bottom = 0;
    canvas.style.left = 0;
    canvas.style.top = 0;
    canvas.style.opacity = 1;
    canvas.width = rect.width;
    canvas.height = rect.height;

    /** Setting styles. */
    ctx2d.strokeStyle = polygonDefaults.strokeColor;
    ctx2d.lineWidth = polygonDefaults.strokeWidth;

    y.panes.get('overlaps').getElement().append(canvas);

    const onDown = (e) => {
      isDrawing = true;

      coordinates.push([e.pageX, e.pageY]);
      offsets.push([e.offsetX || e.clientX - rect.left, e.offsetY || e.clientY - rect.top]);
    };
    const onMove = (e) => {
      if (!isDrawing) return;

      const last = offsets[offsets.length - 1];

      ctx2d.beginPath();
      ctx2d.moveTo(last[0], last[1]);
      ctx2d.lineTo(e.offsetX || e.clientX - rect.left, e.offsetY || e.clientY - rect.top);
      ctx2d.stroke();

      coordinates.push([e.pageX, e.pageY]);
      offsets.push([e.offsetX || e.clientX - rect.left, e.offsetY || e.clientY - rect.top]);
    };
    const onUp = () => {
      canvas.parentNode.removeChild(canvas);
      isDrawing = false;

      const projection = y.options.get('projection');
      coordinates = coordinates.map((x) =>
        projection.fromGlobalPixels(y.converter.pageToGlobal([x[0], x[1]]), y.getZoom())
      );

      return coordinates;
    };

    canvas.onmousedown = (e) => onDown(e);
    canvas.ontouchstart = (e) => {
      e.preventDefault();
      onDown(e.touches[0]);
    };

    canvas.onmousemove = (e) => onMove(e);
    canvas.ontouchmove = (e) => {
      e.preventDefault();
      onMove(e.touches[0]);
    };

    return new Promise((resolve) => {
      /** When mouse up saving coordinates and hiding canvas. */
      canvas.onmouseup = (e) => {
        if (coordinates.length > 10) resolve(onUp(e));

        ctx2d.clearRect(0, 0, canvas.width, canvas.height);
        offsets = [];
        coordinates = [];
        isDrawing = false;
      };
      canvas.ontouchend = (e) => {
        if (coordinates.length > 10) resolve(onUp(e));

        ctx2d.clearRect(0, 0, canvas.width, canvas.height);
        offsets = [];
        coordinates = [];
        isDrawing = false;
      };
    });
  };
  const handleCatalogBackClick = () => {
    let newLink = '/objects';

    switch (objectsState.filter.type) {
      default:
      case 'продажа':
        newLink += '/buy';
        break;

      case 'аренда':
        newLink += '/rent';
        break;
    }
    switch (objectsState.filter.category) {
      default:
      case 'квартира':
        newLink += '/flats';
        break;

      case 'комната':
        newLink += '/rooms';
        break;

      case 'дом':
        newLink += '/houses';
        break;

      case 'участок':
        newLink += '/sites';
        break;

      case 'коммерция':
        newLink += '/commercial';
        break;
    }
    if (!objectsState.filter.studio && objectsState.filter.rooms.length === 1) {
      switch (objectsState.filter.rooms[0]) {
        case '1':
          newLink += '/1k';
          break;

        case '2':
          newLink += '/2k';
          break;

        case '3':
          newLink += '/3k';
          break;

        case '4':
          newLink += '/4k';
          break;

        default:
          break;
      }
    } else if (objectsState.filter.studio && !objectsState.filter.rooms.length) {
      newLink += '/studio';
    }

    setObjectsState({
      type: 'SET_FILTER',
      payload: {
        ...objectsState.filter,
        ads: []
      }
    });

    router.push(
      `${newLink}${JSONToQuery(
        { ...objectsState.filter, ...objectsState.pagination, ...objectsState.sorting },
        [
          'type',
          'category',
          'length',
          'ads',
          objectsState.filter.studio && !objectsState.filter.rooms.length && 'studio',
          !objectsState.filter.studio && objectsState.filter.rooms.length === 1 && 'rooms'
        ],
        ['page=1', 'sort=last-update.date'],
        true
      )}`
    );
  };
  const handleMapLoad = (e) => {
    setYmaps(e);
    setIsMapReady(true);
    setIsLoading(false);

    setObjectsState({
      type: 'SET_MAP',
      payload: {
        ...objectsState.map,
        bbox: mapId.current.getBounds()
      }
    });
  };
  const handleOnBoundsChange = (e) => {
    setZoomLevel(e.originalEvent.newZoom);
    setObjectsState({
      type: 'SET_MAP',
      payload: {
        ...objectsState.map,
        bbox: mapId.current.getBounds(),
        zoomLevel: e.originalEvent.newZoom
      }
    });

    router.replace(
      `/objects/map${JSONToQuery(
        {
          ...objectsState.filter,
          ...objectsState.map
        },
        ['bbox', 'zoomLevel', 'ads'],
        [],
        true
      )}`
    );
  };
  const handleClickOnPoint = (e) => {
    const currentZoom = mapId.current.getZoom();
    const target =
      e.originalEvent.currentTarget.clusters.getById(e.get('objectId')) ||
      e.originalEvent.currentTarget.objects.getById(e.get('objectId'));

    if (currentZoom < 14.5) {
      mapId.current.setCenter(
        target.type === 'Cluster'
          ? ymaps.util.bounds.getCenter(target.bbox)
          : target.geometry.coordinates,
        15,
        { duration: 300 }
      );
    } else {
      setClickedActivePoint(target);
      setObjectsState({
        type: 'SET_FILTER',
        payload: { ...objectsState.filter, ads: target.properties.clusterObjects || [target.id] }
      });
      setObjectsState({
        type: 'SET_STATE',
        payload: { ...objectsState.state, isMapCatalogVisible: true }
      });

      router.replace(
        `/objects/map${JSONToQuery(
          {
            ...objectsState.filter,
            ...objectsState.map,
            ads: target.properties.clusterObjects || [target.id]
          },
          ['bbox', 'zoomLevel', 'ads'],
          [],
          true
        )}`
      );
    }
  };
  const handleDrawButtonClick = () => {
    setIsDrawing(!isDrawing);
    setDrawPolygon([]);

    setObjectsState({
      type: 'SET_MAP',
      payload: {
        ...objectsState.map,
        searchZone: []
      }
    });
  };

  useEffect(() => {
    if (isMount) return;

    if (isDrawing) {
      /** If want to draw. */
      drawCustomPolygon(mapId.current).then((coordinates) => {
        const cuttedCoordinates = coordinates.filter((_, i) => i % 3 === 0);
        const unkinkPolygons = unkinkPolygon(
          {
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [cuttedCoordinates]
            }
          },
          { tolerance: 1e-7 }
        );
        const cleanedPolygon = cleanCoords(buffer(combine(unkinkPolygons), 1e-12).features[0])
          .geometry.coordinates;
        const sizedPolygon = cleanedPolygon[0].map((v) => [
          Number(v[0].toFixed(5)),
          Number(v[1].toFixed(5))
        ]);

        const newMapState = {
          ...objectsState.map,
          searchZone: sizedPolygon
        };

        setDrawPolygon(sizedPolygon);
        setObjectsState({
          type: 'SET_MAP',
          payload: newMapState
        });

        router.replace(
          `/objects/map${JSONToQuery(
            { ...objectsState.filter, ...newMapState },
            ['bbox', 'zoomLevel', 'ads'],
            [],
            true
          )}`
        );
      });
    } else {
      setDrawPolygon([]);
      setObjectsState({
        type: 'SET_MAP',
        payload: {
          ...objectsState.map,
          searchZone: []
        }
      });

      router.replace(
        `/objects/map${JSONToQuery(
          {
            ...objectsState.filter,
            ...objectsState.map,
            searchZone: []
          },
          ['bbox', 'zoomLevel', 'ads'],
          [],
          true
        )}`
      );
    }
  }, [isDrawing]);
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    if (!objectsState.filter.ads.length) setClickedActivePoint();

    const fetchData = async () => {
      try {
        const request = await axios.post(
          process.env.NEXT_PUBLIC_API + '/objects/map',
          {
            ...objectsState.filter,
            bbox: objectsState.map.bbox,
            zoom: objectsState.map.zoomLevel,
            searchZone: objectsState.map.searchZone,
            ads: []
          },
          { signal }
        );

        if (!request.data)
          throw new Error('Something wrong with getting server data! [ObjectsMap -> fetchData()]');

        setObjectManagerFeatures(request.data.features);
      } catch (error) {}
    };
    fetchData();
  }, [objectsState.filter, objectsState.map.bbox, objectsState.map.searchZone]);
  useEffect(() => {
    if (isMount || drawPolygon.length) return;

    if (mapId.current) {
      mapId.current.setCenter(locationState.coordinates, locationState.zoom);
    }
  }, [locationState]);

  return (
    <section className={['block', 'clear', styles.map].join(' ')}>
      {isLoading && !isMapReady && (
        <div className={styles.loading}>
          <img src="/images/man-with-hand.svg" alt="" />
          <div>Загрузка картографических данных...</div>
        </div>
      )}
      <YMaps query={{ apikey: process.env.NEXT_PUBLIC_YANDEX_KEY }}>
        <TemplateProvider>
          {() => (
            <Map
              modules={['util.bounds']}
              style={{ width: '100%', height: '100%' }}
              defaultState={{
                center: locationState.coordinates,
                zoom: locationState.zoom,
                controls: [],
                behaviors: ['drag', 'dblClickZoom', 'multiTouch', 'scrollZoom'],
                margin: [0, 0, 0, width > 992 && objectsState.state.isMapCatalogVisible ? 420 : 0]
              }}
              defaultOptions={{
                maxZoom: 20,
                maxAnimationZoomDifference: Infinity,
                autoFitToViewport: 'always',
                yandexMapDisablePoiInteractivity: true,
                suppressObsoleteBrowserNotifier: true,
                suppressMapOpenBlock: true
              }}
              onLoad={handleMapLoad}
              onBoundschange={handleOnBoundsChange}
              instanceRef={(ref) => {
                if (ref) {
                  mapId.current = ref;
                }
              }}
            >
              {isMapReady && (
                <Fragment>
                  {clickedActivePoint && (
                    <Placemark
                      geometry={clickedActivePoint.geometry.coordinates}
                      options={
                        clickedActivePoint.type === 'Cluster'
                          ? {
                              iconLayout: ymaps.templateLayoutFactory.createClass(`
                          <div class="point default cluster active">
                            <div class="information">от ${clickedActivePoint.priceText}</div>
                            ${clickedActivePoint.properties.clusterInfo}
                          </div>`),
                              iconShape:
                                zoomLevel > 14.5
                                  ? {
                                      type: 'Rectangle',
                                      coordinates: [
                                        [-38, -26],
                                        [62, 22]
                                      ]
                                    }
                                  : {
                                      type: 'Circle',
                                      coordinates: [12, 12],
                                      radius: 11
                                    },
                              iconOffset: [-12, -12],
                              zIndex: 10
                            }
                          : {
                              iconLayout: ymaps.templateLayoutFactory.createClass(
                                `<div class="point default active">
                            <div class="information">${clickedActivePoint.priceText}</div>
                          </div>`
                              ),
                              iconShape:
                                zoomLevel > 14.5
                                  ? {
                                      type: 'Rectangle',
                                      coordinates: [
                                        [-28, -26],
                                        [52, 14]
                                      ]
                                    }
                                  : {
                                      type: 'Circle',
                                      coordinates: [8, 8],
                                      radius: 7
                                    },
                              iconOffset: [-6, -6],
                              zIndex: 10
                            }
                      }
                    />
                  )}
                  {Boolean(drawPolygon.length) && (
                    <Polygon geometry={[drawPolygon]} options={polygonDefaults} />
                  )}
                  <ObjectManager
                    objects={{
                      iconLayout: ymaps.templateLayoutFactory.createClass(
                        zoomLevel > 14.5
                          ? `
                          <div class="point default">
                            {% if price > 0 %}
                            <div class="information">{{priceText}}</div>
                            {% endif %}
                          </div>
                        `
                          : `
                          <div class="point default"></div>
                        `
                      ),
                      iconShape:
                        zoomLevel > 14.5
                          ? {
                              type: 'Rectangle',
                              coordinates: [
                                [-28, -26],
                                [52, 14]
                              ]
                            }
                          : {
                              type: 'Circle',
                              coordinates: [8, 8],
                              radius: 7
                            },
                      iconOffset: [-6, -6],
                      hasBalloon: false,
                      zIndex: 6
                    }}
                    clusters={{
                      clusterIconLayout: ymaps.templateLayoutFactory.createClass(
                        zoomLevel > 14.5
                          ? `
                          <div class="point default cluster">
                            {% if price > 0 %}
                            <div class="information">от {{priceText}}</div>
                            {% endif %}
                            <div class="count">
                              {% if properties.clusterInfo < 100 %}
                              {{properties.clusterInfo}}
                              {% else %}
                              99
                              {% endif %}
                            </div>
                          </div>
                        `
                          : `
                          <div class="point default cluster">
                            <div class="count">
                              {% if properties.clusterInfo < 100 %}
                              {{properties.clusterInfo}}
                              {% else %}
                              99
                              {% endif %}
                            </div>
                          </div>
                        `
                      ),
                      clusterIconShape:
                        zoomLevel > 14.5
                          ? {
                              type: 'Rectangle',
                              coordinates: [
                                [-38, -26],
                                [62, 22]
                              ]
                            }
                          : {
                              type: 'Circle',
                              coordinates: [12, 12],
                              radius: 11
                            },
                      clusterIconOffset: [-12, -12],
                      zIndex: 6
                    }}
                    options={{
                      clusterize: false,
                      clusterDisableClickZoom: true
                    }}
                    features={objectManagerFeatures}
                    onClick={handleClickOnPoint}
                  />
                </Fragment>
              )}
              <Button
                options={{
                  position:
                    width > 992 ? { top: 12, right: 72 } : { top: 12, left: 'calc(50% - 89px)' },
                  maxWidth: 'none',
                  selectOnClick: false
                }}
                data={{
                  content: '<div style="margin: 0 18px;">Вернуться к списку</div>'
                }}
                onClick={handleCatalogBackClick}
              />
              <Button
                options={{
                  position: width > 992 ? { top: 132, right: 12 } : { bottom: 32, right: 12 },
                  selectOnClick: false
                }}
                data={{
                  content: `${
                    isAnyFilters(objectsState.filter)
                      ? `<span style="position: absolute; right: 8px; bottom: 8px; background-color: #fb5f5f; width: 8px; height: 8px; border-radius: 50%;"></span>`
                      : ''
                  }<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>`
                }}
                onClick={() =>
                  setObjectsState({
                    type: 'SET_STATE',
                    payload: { ...objectsState.state, isFiltersMobileVisible: true }
                  })
                }
              />
              <GeolocationControl
                options={{
                  position: width > 992 ? { right: 12, top: 12 } : { right: 12, top: 12 }
                }}
              />
              <Button
                options={{
                  position: width > 992 ? { top: 72, right: 12 } : { bottom: 90, right: 12 }
                }}
                data={{
                  content:
                    Boolean(drawPolygon.length) || isDrawing
                      ? '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'
                      : '<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24" fill="none"><path d="m8.196 12-1 4 4-1 7.293-7.293a1 1 0 0 0 0-1.414l-1.586-1.586a1 1 0 0 0-1.414 0L8.196 12zM16.196 7l-6 6" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/><path d="m5.197 20.5.185-.464a.458.458 0 0 0-.027-.01l-.158.474zm12.519.637a.5.5 0 0 0 .961-.274l-.961.274zm-11.729-3.73a.5.5 0 0 0-.58-.814l.58.814zm-.976 3.557c.771.309 1.473.224 2.122-.05.62-.263 1.219-.71 1.798-1.137C10.123 18.9 11.378 18 13.197 18v-1c-2.181 0-3.677 1.1-4.86 1.973-.608.448-1.104.813-1.593 1.02-.46.194-.883.234-1.362.043l-.371.928zM13.197 18c1.83 0 2.949.792 3.62 1.575a4.991 4.991 0 0 1 .86 1.447 2.606 2.606 0 0 1 .038.114l.001.003v-.001l.48-.138a44.244 44.244 0 0 0 .481-.139v-.001l-.002-.004-.003-.012a4.63 4.63 0 0 0-.057-.167 5.998 5.998 0 0 0-1.039-1.753c-.829-.966-2.21-1.924-4.38-1.924v1zm-8 2.5a48.868 48.868 0 0 1 .16-.474h-.002a2.133 2.133 0 0 1-.32-.152c-.193-.114-.37-.261-.464-.43-.076-.136-.12-.325.023-.628.158-.334.548-.805 1.393-1.41l-.58-.813c-.906.646-1.453 1.238-1.717 1.794-.278.588-.23 1.118.007 1.543.22.395.573.654.833.806a3.128 3.128 0 0 0 .492.232l.01.004.004.002h.002l.159-.474z" fill="currentColor"/></svg>'
                }}
                defaultState={{ selected: Boolean(drawPolygon.length) || isDrawing }}
                instanceRef={(ref) => {
                  if (ref) drawButtonId.current = ref;
                }}
                onClick={handleDrawButtonClick}
              />
              <ZoomControl
                options={{
                  position: { right: '12px', top: 'calc(50vh - 104px)' },
                  size: 'small'
                }}
              />
            </Map>
          )}
        </TemplateProvider>
        {width > 992 && isMapReady && <ObjectsMapCatalog />}
        {width < 992 && isMapReady && <ObjectsMapCatalogMobile />}
      </YMaps>
    </section>
  );
};

export default ObjectsMap;
